'use client';

import React, { useCallback, useMemo } from 'react';
import Link from 'next/link';
import { Star, ImageIcon } from 'lucide-react';
import { useTopListingsForHome } from '@/hooks/useTopListingsForHome';
import { AppImage } from '@/components/ui/AppImage';
import { adaptTopListingCard, type V2ListingCard } from '../../adapters/listingAdapter';
import { SectionWrapper, SectionHeading, Button } from '../../components/common';

export interface ProductGridProps {
  /** Same default as main home `TopListings` — `get_top_listings_for_home` RPC. */
  limit?: number;
}

const StarRating: React.FC<{ rating: number; reviews: number }> = ({ rating, reviews }) => (
  <div className="mt-1 flex items-center gap-1">
    <Star size={13} fill="#1E8C3A" stroke="none" />
    <span className="text-[13px] font-semibold leading-none text-v2-green-500">
      {Number.isFinite(rating) ? rating.toFixed(1) : '0.0'}
    </span>
    <span className="text-[12px] text-v2-neutral-400">({reviews})</span>
  </div>
);

export const ProductGrid: React.FC<ProductGridProps> = ({ limit = 4 }) => {
  const { data, isLoading, isError, refetch, isFetching } = useTopListingsForHome(limit);

  const items: V2ListingCard[] = useMemo(
    () => (data?.listings ?? []).map(adaptTopListingCard),
    [data?.listings]
  );

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  return (
    <SectionWrapper bg="white" py="lg" className="pt-0">
      <SectionHeading title="Top Rented Items" />

      {isLoading && items.length === 0 ? (
        <div className="mb-8 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-v2-xl border border-v2-neutral-200">
              <div className="aspect-[4/3] animate-pulse bg-v2-neutral-100" />
              <div className="space-y-2 p-3.5">
                <div className="h-4 w-[85%] animate-pulse rounded bg-v2-neutral-100" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-v2-neutral-100" />
                <div className="h-5 w-1/3 animate-pulse rounded bg-v2-neutral-100" />
              </div>
            </div>
          ))}
        </div>
      ) : isError && items.length === 0 ? (
        <div className="py-10 text-center">
          <p className="mb-4 text-sm text-v2-neutral-600">Unable to load listings at the moment.</p>
          <button
            type="button"
            onClick={handleRetry}
            className="text-sm font-semibold text-v2-green-600 hover:text-v2-green-700"
          >
            Try again
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="py-10 text-center text-sm text-v2-neutral-500">No listings available at the moment.</p>
      ) : (
        <div className="relative mb-8">
          {isFetching && items.length > 0 && (
            <div
              className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-v2-xl bg-v2-neutral-0/50"
              aria-hidden
            >
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-v2-green-500 border-t-transparent" />
            </div>
          )}
          <div
            className={`grid grid-cols-2 gap-5 lg:grid-cols-4 ${isFetching ? 'opacity-70' : ''}`}
          >
            {items.map((item) => (
              <Link key={item.id} href={`/listings/${item.id}`} className="group flex h-full">
                <div className="flex h-full w-full flex-col overflow-hidden rounded-v2-xl border border-v2-neutral-200 bg-v2-neutral-0 transition-shadow duration-200 hover:shadow-md">
                  <div className="relative aspect-[4/3] overflow-hidden bg-v2-neutral-100">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-v2-neutral-300">
                        <ImageIcon size={40} strokeWidth={1.2} />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-3.5">
                    <h4 className="line-clamp-2 min-h-[2.75rem] text-[18px] font-semibold leading-snug text-v2-neutral-900">
                      {item.title}
                    </h4>
                    <StarRating rating={item.rating} reviews={item.reviews} />
                    <p className="mt-1.5 font-display text-[20px] font-bold">
                      ${item.pricePerDay}
                      <span className="text-[16px] font-normal text-v2-neutral-800">
                        {' '}
                        / {item.rentalPeriod ?? 'day'}
                      </span>
                    </p>
                    <div className="flex-1" aria-hidden />
                    {item.ownerName && (
                      <div className="mt-2 flex min-w-0 items-center gap-2 border-t border-v2-neutral-100 pt-2">
                        <div className="relative h-6 w-6 flex-shrink-0 overflow-hidden rounded-full bg-v2-neutral-100">
                          <AppImage
                            src={item.ownerAvatarUrl}
                            alt={item.ownerName}
                            fill
                            className="object-cover"
                            sizes="24px"
                          />
                        </div>
                        <span className="min-w-0 truncate text-[13px] text-v2-neutral-600">
                          Shared by {item.ownerName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="text-center">
        <Button variant="secondary" as="a" href="/browse">
          Browse All Items
        </Button>
      </div>
    </SectionWrapper>
  );
};
